import * as React from "react";
import Queue from "./Queue";
import ChatController from "./ChatController";
import IntroMessageForm from "./IntroMessageForm";
import PreChatSurvey from "./PreChatSurvey";
import PostChatSurvey from "./PostChatSurvey";
import UnavailableSurvey from "./UnavailableSurvey";
import UnavailableHtml from "./UnavailableHtml";
import UnavailableUrl from "./UnavailableUrl";
import PostChatSurveySubmitted from "./PostChatSurveySubmitted";
import UnavailableSurveySubmitted from "./UnavailableSurveySubmitted";
import KBResults from "./KBResults";
import CallWaiting from "./CallWaiting";
import CallView from "./CallView";
import {
  Message,
  ChatUIStage,
  SocialData,
  KBArticle,
  Call,
  QuickReplyButton,
  DataField,
  ActionPopoverContent,
  SecureForm,
  Form
} from "../types";
import FrameLoading from "./FrameLoading";
import Referral from "./Referral";
import ActionPopover from "./ActionPopover";
import Calendly from "./Calendly";
import ErrorView from "./ErrorView";
import useChat from "../hooks/useChat";
import LandingWidgetContainer from "./LandingWidgets/LandingWidgetContainer";
import StoreContainer from "../stores/StoreContainer";
import useConfig from "../hooks/useConfig";
import PoweredBy from "./PoweredBy";
import { useEffect } from "react";
import isMobile from "../helpers/isMobile";
import CloseWindow from "./CloseWindow";

interface Props {
  agentRating: number;
  agentName: string;
  agentHeadshotUrl: string;
  messages: Array<Message>;
  queuePosition: number;
  timeInQueue: number;
  estimatedWait: string;
  unavailableHtml: string;
  unavailableUrl: string;
  waitingText: string;
  agentsTyping: Array<string>;
  socialData: SocialData;
  kbArticles: Array<KBArticle>;
  visitorId: string;
  engagementId: string;
  engagementApiEndpoint: string;
  maxFileSizeBytes: number;
  fileSizeErrorMessage: string;
  siteId: number;
  isPopout: boolean;
  cobrowseState: string;
  onCollapse: () => void;
  onPopout: () => void;
  onCloseChat: () => void;
  onClose: () => void;
  onSendIntroMessage: (introMessage: string) => void;
  onSendMessage: (text: string) => void;
  onclickQuickReply: (button: QuickReplyButton) => void;
  onSubmitDataField: (value: string) => void;
  onSubmitForm: (values: any) => void;
  onSetAgentRating: (rating: number) => void;
  onSubmitEmailAddress: (emailAddress: string) => void;
  onSubmitPreChatSurvey: (answers: any) => void;
  onSubmitPostChatSurvey: (answers: any) => void;
  onSubmitUnavailableSurvey: (answers: any) => void;
  onTyping: (message: string) => void;
  onSetLineRating: (lineId: number, rating: number) => void;
  onContinueFromKnowledgeBase: () => void;
  onVisitorEndCall: () => void;
  onResendMessage(message: Message): void;
  onClickActionCard: (name: string, data: string) => void;
  onCollapsePopover: () => void;
  onRetryFromError: () => void;
  call: Call;
  onCallForText: string;
  quickReplies: QuickReplyButton[];
  dataField: DataField;
  form: Form;
  actionPopoverContent: ActionPopoverContent;
  messageFormDisabled: boolean;
  errorMessage: string;
  secureForm: SecureForm;
}

function Messenger(props: Props) {
  const { chatStage } = useChat();
  const config = useConfig();

  useEffect(() => {
    const clicked = () => {
      StoreContainer.FrameStore.collapse();
    };
    if (config.ExpandChatWindowMobile || !isMobile()) {
      return;
    }

    window.parent.document.body.addEventListener("click", clicked);
    return () => {
      window.parent.document.body.removeEventListener("click", clicked);
    };
  }, [config.ExpandChatWindowMobile]);

  function renderLandingWidgets() {
    const landingWidgetsConfig = config.LandingWidgetsConfig;
    if (!landingWidgetsConfig) {
      console.error("Missing landing widget configuration!");
      StoreContainer.FrameStore.collapse();
    }

    return (
      <LandingWidgetContainer
        onCollapse={() => StoreContainer.FrameStore.collapse()}
        onStartChat={() => StoreContainer.EngagementStore.startEngagement()}
        config={landingWidgetsConfig}
      />
    );
  }

  function renderContent() {
    switch (chatStage) {
      case ChatUIStage.Loading:
        return <FrameLoading />;
      case ChatUIStage.LandingWidgets:
        return renderLandingWidgets();
      case ChatUIStage.PreChatSurvey:
        return (
          <PreChatSurvey
            isPopout={props.isPopout}
            onCollapse={props.onCollapse}
            onSubmit={props.onSubmitPreChatSurvey}
            socialData={props.socialData}
          />
        );
      case ChatUIStage.IntroForm:
        return (
          <IntroMessageForm
            isPopout={props.isPopout}
            onCollapse={props.onCollapse}
            onSubmit={props.onSendIntroMessage}
            onClose={props.onClose}
          />
        );
      case ChatUIStage.Queued:
        return (
          <Queue
            position={props.queuePosition}
            timeInQueue={props.timeInQueue}
            estimatedWait={props.estimatedWait}
            onPopout={props.onPopout}
            onCollapse={props.onCollapse}
            onClose={props.onClose}
            isPopout={props.isPopout}
          />
        );
      case ChatUIStage.Assigned:
      case ChatUIStage.AssignedToBot:
        return (
          <ChatController
            agentRating={props.agentRating}
            secureForm={props.secureForm}
            onSetAgentRating={props.onSetAgentRating}
            agentName={props.agentName}
            agentHeadshotUrl={props.agentHeadshotUrl}
            onSendMessage={props.onSendMessage}
            quickReplies={props.quickReplies}
            onClickQuickReply={props.onclickQuickReply}
            onSubmitDataField={props.onSubmitDataField}
            onSubmitForm={props.onSubmitForm}
            messages={props.messages}
            onPopout={props.onPopout}
            onCollapse={props.onCollapse}
            onClose={props.onCloseChat}
            onSubmitEmailAddress={props.onSubmitEmailAddress}
            onTyping={props.onTyping}
            agentsTyping={props.agentsTyping}
            visitorId={props.visitorId}
            engagementId={props.engagementId}
            engagementApiEndpoint={props.engagementApiEndpoint}
            maxFileSizeBytes={props.maxFileSizeBytes}
            fileSizeErrorMessage={props.fileSizeErrorMessage}
            siteId={props.siteId}
            onSetLineRating={props.onSetLineRating}
            isPopOut={props.isPopout}
            onResendMessage={props.onResendMessage}
            dataField={props.dataField}
            form={props.form}
            onClickActionCard={props.onClickActionCard}
            messageFormDisabled={props.messageFormDisabled}
            cobrowseState={props.cobrowseState}
          />
        );
      case ChatUIStage.PostChatSurvey:
        return (
          <PostChatSurvey
            isPopout={props.isPopout}
            onCollapse={props.onCollapse}
            onClose={props.onClose}
            onSubmit={props.onSubmitPostChatSurvey}
            socialData={props.socialData}
            agentRating={props.agentRating}
          />
        );
      case ChatUIStage.UnavailableHtml:
        return (
          <UnavailableHtml
            isPopout={props.isPopout}
            html={props.unavailableHtml}
            onClose={props.onClose}
            onCollapse={props.onCollapse}
            onPopout={props.onPopout}
          />
        );
      case ChatUIStage.UnavailableSurvey:
        return (
          <UnavailableSurvey
            isPopout={props.isPopout}
            onPopout={props.onPopout}
            onCollapse={props.onCollapse}
            onSubmit={props.onSubmitUnavailableSurvey}
            socialData={props.socialData}
            onClose={props.onClose}
          />
        );
      case ChatUIStage.UnavailableUrl:
        return (
          <UnavailableUrl
            isPopout={props.isPopout}
            url={props.unavailableUrl}
            onClose={props.onClose}
            onCollapse={props.onCollapse}
            onPopout={props.onPopout}
          />
        );
      case ChatUIStage.PostChatSurveySubmitted:
        return (
          <PostChatSurveySubmitted
            isPopout={props.isPopout}
            onClose={props.onClose}
          />
        );
      case ChatUIStage.Referral:
        return <Referral isPopout={props.isPopout} />;
      case ChatUIStage.UnavailableSurveySubmitted:
        return (
          <UnavailableSurveySubmitted
            isPopout={props.isPopout}
            onClose={props.onClose}
          />
        );
      case ChatUIStage.KBResults:
        return (
          <KBResults
            isPopout={props.isPopout}
            kbArticles={props.kbArticles}
            onClose={props.onClose}
            onCollapse={props.onCollapse}
            onPopout={props.onPopout}
            onContinueFromKnowledgeBase={props.onContinueFromKnowledgeBase}
          />
        );
      case ChatUIStage.CallWaiting:
        return (
          <CallWaiting
            onClose={props.onClose}
            onCollapse={props.onCollapse}
            onPopout={props.onPopout}
            isPopout={props.isPopout}
            waitingText={props.waitingText}
            onVisitorEndCall={props.onVisitorEndCall}
          />
        );
      case ChatUIStage.Call:
        return (
          <CallView
            call={props.call}
            onCallForText={props.onCallForText}
            onPopout={props.onPopout}
            onCollapse={props.onCollapse}
            onClose={props.onClose}
            onVisitorEndCall={props.onVisitorEndCall}
            isPopOut={props.isPopout}
          />
        );
      case ChatUIStage.CloseWindow:
        return <CloseWindow />;
      default:
        throw new Error(`unhandled chatStage ${chatStage}`);
    }
  }

  function getActionPopoverContent() {
    switch (props.actionPopoverContent.type) {
      case "calendly":
        return (
          <Calendly
            calendarName={props.actionPopoverContent.data.calenderName}
            onBookMeeting={props.actionPopoverContent.data.onBookMeeting}
          />
        );
    }
  }

  function renderActionPopover() {
    if (!props.actionPopoverContent) {
      return null;
    }
    return (
      <ActionPopover onCollapse={props.onCollapsePopover}>
        {getActionPopoverContent()}
      </ActionPopover>
    );
  }

  return (
    <div className="velaro-messenger-flex" role="dialog">
      <h1 className="velaro-skip">Live Chat</h1>
      {renderActionPopover()}

      {props.errorMessage && (
        <ErrorView
          message={props.errorMessage}
          isPopout={props.isPopout}
          onCollapse={props.onCollapse}
          onClickRetry={props.onRetryFromError}
        />
      )}
      {!props.errorMessage && renderContent()}
    </div>
  );
}

export default Messenger;
